import React, { useEffect, useState } from "react";
import { InfoTip } from "@/components/InfoTip";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { School, Download, CreditCard, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const btn = "font-mono uppercase tracking-[0.14em] text-[11px] px-5 py-3 border-2 border-[#002FA7] transition-colors flex items-center justify-center gap-2 disabled:opacity-50";
const input = "w-full border border-border bg-background px-3 py-2 text-sm";

export const downloadQuote = async (id, number) => {
  try {
    const { data } = await api.get(`/school/quotes/${id}/pdf`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(data);
    a.download = `${number}.pdf`;
    a.click();
  } catch { toast.error("Téléchargement du devis impossible."); }
};

// Offre établissement: quote request (bank transfer or card). Hidden until the admin opens it.
export default function SchoolOffer() {
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [f, setF] = useState({ school: "", address: "", contact: "", package_id: "school_1000", method: "virement", po_number: "" });
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [agreed, setAgreed] = useState(false);
  useEffect(() => { api.get("/school/offer").then(({ data }) => setOffer(data)).catch(() => setOffer({ open: false, packs: [] })); }, []);
  const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/school/quotes", f);
      setQuote({ ...data, package_id: f.package_id });
      toast.success(`Devis ${data.number} créé et envoyé par e-mail.`);
    } catch (err) { toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Création du devis impossible."); }
    finally { setBusy(false); }
  };
  const payCard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/checkout", { package_id: quote.package_id, quote_id: quote.id, origin_url: window.location.origin, accepted_terms: true });
      window.location.href = data.url;
    } catch (err) { toast.error(err?.response?.data?.detail || "Paiement indisponible."); setBusy(false); }
  };
  if (!offer) return null;
  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="school-offer-page">
      <div className="sux-marker mb-3 flex items-center gap-2"><School className="w-3.5 h-3.5" /> Offre établissement <InfoTip testid="school-offer-info" content={"Les crédits vont sur un seul compte (direction ou professeur référent). Moins chers que les packs individuels, sans date limite."} /></div>
      <h1 className="text-4xl sm:text-5xl font-black mb-4" style={{ fontFamily: "Outfit" }}>SUX1 pour toute votre école</h1>
      <p className="text-muted-foreground mb-8">Des crédits pour toute l'équipe, payables sur devis : virement administratif ou carte. Sans abonnement, sans date limite.</p>
      {!offer.open ? (
        <div className="border-2 border-[#002FA7] p-6" data-testid="school-offer-soon">
          <p className="font-bold mb-2">Bientôt disponible.</p>
          <p className="text-sm text-muted-foreground">Pour être prévenu de l'ouverture, écrivez-nous avec le lien « Contact » en bas de page. <Link to="/" className="underline">Retour à l'accueil</Link></p>
        </div>
      ) : !user || user.is_anonymous ? (
        <div className="border-2 border-[#002FA7] p-6" data-testid="school-offer-login">
          <p className="mb-3">Créez ou connectez le compte SUX1 qui recevra les crédits (par exemple celui de la direction ou du professeur référent).</p>
          <Link to="/login?next=/etablissements/devis" className="underline font-bold" data-testid="school-offer-login-link">Se connecter</Link>
        </div>
      ) : (
        <>
          <p className="text-sm mb-2 flex items-center gap-1">1. Choisissez un pack <InfoTip testid="school-packs-info" content={"1 crédit = 1 résumé standard (jusqu'à environ 20 pages). Exemple : 1 000 crédits = environ 1 000 résumés, fiches DYS ou cartes mentales."} /></p>
          <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="school-offer-packs">
            {offer.packs.map((p) => (
              <button key={p.id} type="button" disabled={!!quote} onClick={() => setF((o) => ({ ...o, package_id: p.id }))} data-testid={`school-pack-${p.id}`}
                className={`border-2 p-4 text-left transition-colors ${f.package_id === p.id ? "border-[#002FA7] bg-[#002FA7]/5" : "border-border hover:border-[#002FA7]"}`}>
                <div className="text-2xl font-black" style={{ fontFamily: "Outfit" }}>{p.amount.toFixed(0)} €</div>
                <div className="text-sm">{p.credits.toLocaleString("fr-FR")} crédits</div>
                <div className="text-[11px] text-muted-foreground mt-1">{((p.amount / p.credits) * 100).toFixed(1)} centimes le crédit</div>
              </button>
            ))}
          </div>
          {!quote ? (
            <form onSubmit={submit} className="space-y-3" data-testid="school-quote-form">
              <p className="text-sm flex items-center gap-1">2. Remplissez le devis <InfoTip testid="school-form-info" content={"Ces informations apparaissent sur le devis et la facture. Le n° de bon de commande est utile si votre gestionnaire en demande un."} /></p>
              <input className={input} placeholder="Nom de l'établissement" value={f.school} onChange={set("school")} required data-testid="school-quote-school" />
              <textarea className={input} rows={2} placeholder="Adresse de facturation" value={f.address} onChange={set("address")} required data-testid="school-quote-address" />
              <input className={input} placeholder="Nom du contact" value={f.contact} onChange={set("contact")} required data-testid="school-quote-contact" />
              <input className={input} placeholder="N° de bon de commande (facultatif)" value={f.po_number} onChange={set("po_number")} data-testid="school-quote-po" />
              <select className={input} value={f.method} onChange={set("method")} data-testid="school-quote-method">
                <option value="virement">Paiement par virement (crédits ajoutés à réception)</option>
                <option value="carte">Paiement par carte en ligne (crédits immédiats)</option>
              </select>
              <p className="text-[12px] text-muted-foreground flex items-center gap-1">Virement ou carte ? <InfoTip testid="school-method-info" content={"Virement : vous recevez le devis avec notre IBAN ; les crédits arrivent quand le paiement est reçu. Carte : paiement en ligne juste après le devis, crédits immédiats."} /></p>
              <p className="text-[12px] text-muted-foreground">Crédits ajoutés au compte : <b>{user.email}</b></p>
              <button type="submit" disabled={busy} className={`${btn} bg-[#002FA7] text-white w-full`} data-testid="school-quote-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Recevoir mon devis PDF
              </button>
            </form>
          ) : (
            <div className="border-2 border-[#002FA7] p-6 space-y-4" data-testid="school-quote-done">
              <p>Devis <b>{quote.number}</b> envoyé à {user.email}.</p>
              <button type="button" onClick={() => downloadQuote(quote.id, quote.number)} className={`${btn} text-[#002FA7]`} data-testid="school-quote-download"><Download className="w-4 h-4" /> Télécharger le devis PDF</button>
              {f.method === "carte" && (
                <>
                  <label className="flex items-start gap-2 text-sm">
                    <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="school-quote-cgv" />
                    <span>J'accepte les <Link to="/cgv" className="underline">CGV</Link> et demande l'accès immédiat aux crédits.</span>
                  </label>
                  <button type="button" onClick={payCard} disabled={!agreed || busy} className={`${btn} bg-[#002FA7] text-white`} data-testid="school-quote-pay-card"><CreditCard className="w-4 h-4" /> Payer par carte</button>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
